import React, {
  InputHTMLAttributes,
  RefObject,
  useCallback,
  useMemo,
  useState,
  KeyboardEvent,
} from 'react'
import { NumericKeypadFloatRegex, NumericKeypadRegex } from 'root-constants'
import { StyledInput as S } from './Input.styles'

type TProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  hasVisibilityControl?: boolean
  isValid?: boolean
  validationText?: string
  inputRef?: RefObject<HTMLInputElement>
  allowFloatNumbers?: boolean
  className?: string
  maxLength?: number
  isContentCentered?: boolean
}

export const Input: React.FC<TProps> = ({
  label,
  hasVisibilityControl = false,
  isValid = true,
  validationText = '',
  inputRef,
  allowFloatNumbers = true,
  className,
  type,
  maxLength = 30,
  isContentCentered = false,
  value,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false)

  const inputType = useMemo(() => {
    if (!hasVisibilityControl) return type

    return isVisible ? 'text' : 'password'
  }, [hasVisibilityControl, isVisible, type])

  const handleNumberKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        !(
          allowFloatNumbers ? NumericKeypadFloatRegex : NumericKeypadRegex
        ).test(event.key)
      ) {
        event.preventDefault()
      }
    },
    [allowFloatNumbers],
  )

  return (
    <S.Wrapper data-valid={isValid} data-validation-text={validationText}>
      <S.Input
        className={className}
        hasLabel={Boolean(label)}
        hasButton={hasVisibilityControl}
        isContentCentered={isContentCentered}
        required
        type={inputType}
        ref={inputRef}
        spellCheck={false}
        maxLength={maxLength}
        onKeyDown={inputType === 'number' ? handleNumberKeyDown : undefined}
        value={value}
        {...props}
      />
      {label && <S.Label isEmpty={!value}>{label}</S.Label>}
      {hasVisibilityControl && (
        <S.VisibilityButton
          type="button"
          onClick={() => setIsVisible((prevValue) => !prevValue)}
          data-visible={isVisible}
        />
      )}
    </S.Wrapper>
  )
}
